/**
 * "My Performance" — staff self-service view of the Performance Management
 * System: goals with live progress, self review submission and the published
 * appraisal (score, band, manager feedback, PDF letter).
 */
import { MyRatingCard } from '../../../components/admin/feedback/RatingBits';
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  Target, Loader2, Send, FileDown, Activity, Lock, Award, LifeBuoy,
  GraduationCap,
} from 'lucide-react';
import { toast } from 'sonner';
import { useAuth } from '../../../contexts/AuthContext';
import { API } from '../../../lib/api';
import { formatErr, inputCls } from '../../../components/admin/billing/shared';
import {
  BAND_STYLE, STATUS_STYLE, statusLabel, Badge, ScoreRing, ProgressBar,
  RatingPicker, fmtMetric,
} from './shared';
import ModuleGuide from './ModuleGuide';
import MySkillsCard from './MySkillsCard';
import { AcknowledgeCard, ScoreHistory } from './AcknowledgeCard';
import { useLang } from '../../../i18n/LangContext';
import { LanguagePicker } from '../../../components/admin/LanguagePicker';

const MyPerformancePage = () => {
  const { authHeader } = useAuth();
  const { lang, t, loading: translating } = useLang();
  const [meta, setMeta] = useState(null);
  const [payload, setPayload] = useState({ staff: null, reviews: [] });
  const [mySkills, setMySkills] = useState(null);
  const [myTraining, setMyTraining] = useState({ learning: [], teaching: [], today: '' });
  const [sectionsLoading, setSectionsLoading] = useState(true);
  const [activeId, setActiveId] = useState('');
  const [draft, setDraft] = useState({ competencies: {}, goal_notes: {}, overall_comment: '' });
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState('');
  const [err, setErr] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    // Blank the skill sheet and the plan while they are re-fetched, otherwise
    // the previous language stays on screen and the page reads half-translated.
    setSectionsLoading(true);
    setMySkills(null);
    setMyTraining({ learning: [], teaching: [], today: '' });
    try {
      const q = `?lang=${lang}`;
      const [{ data: m }, { data }] = await Promise.all([
        axios.get(`${API}/performance/meta${q}`, { headers: authHeader(), timeout: 90000 }),
        axios.get(`${API}/performance/me${q}`, { headers: authHeader(), timeout: 90000 }),
      ]);
      setMeta(m);
      setPayload(data);
      Promise.allSettled([
        axios.get(`${API}/skills/mine${q}`, { headers: authHeader(), timeout: 90000 })
          .then(({ data: sk }) => setMySkills(sk)),
        axios.get(`${API}/skills/training/mine${q}`, { headers: authHeader(), timeout: 90000 })
          .then(({ data: tr }) => setMyTraining(tr)),
      ]).finally(() => setSectionsLoading(false));
      setActiveId((prev) => prev || data.reviews?.[0]?.id || '');
    } catch (e) { setErr(formatErr(e)); setSectionsLoading(false); }
    finally { setLoading(false); }
  }, [authHeader, lang]);

  useEffect(() => { load(); }, [load]);

  const review = useMemo(
    () => payload.reviews.find((r) => r.id === activeId) || payload.reviews[0] || null,
    [payload.reviews, activeId],
  );

  useEffect(() => {
    if (!review) return;
    setDraft({
      competencies: { ...(review.self_competencies || {}) },
      goal_notes: Object.fromEntries((review.goals || []).map((g) => [g.id, g.self_note || ''])),
      overall_comment: review.self_overall_comment || '',
    });
  }, [review]);

  const acknowledge = async (comment) => {
    setBusy('ack');
    try {
      await axios.post(`${API}/performance/reviews/${review.id}/acknowledge`,
        { comment }, { headers: authHeader() });
      toast.success('Acknowledgement recorded');
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const submitSelf = async () => {
    setBusy('self');
    try {
      await axios.post(`${API}/performance/reviews/${review.id}/self`, draft, { headers: authHeader() });
      toast.success('Self review submitted');
      await load();
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const downloadCertificate = async () => {
    setBusy('cert');
    try {
      const res = await axios.get(`${API}/skills/certificate/${payload.staff.id}?lang=${lang}`, {
        headers: authHeader(), responseType: 'blob', timeout: 90000,
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const w = window.open(url, '_blank', 'noopener');
      if (!w) {
        const a = document.createElement('a');
        a.href = url; a.download = 'My_Skill_Certificate.pdf';
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  const downloadPdf = async () => {
    setBusy('pdf');
    try {
      const res = await axios.get(`${API}/performance/reviews/${review.id}/pdf?lang=${lang}`, {
        headers: authHeader(), responseType: 'blob', timeout: 90000,
      });
      const url = URL.createObjectURL(new Blob([res.data], { type: 'application/pdf' }));
      const w = window.open(url, '_blank', 'noopener');
      if (!w) {
        const a = document.createElement('a');
        a.href = url; a.download = 'My_Appraisal.pdf';
        document.body.appendChild(a); a.click(); a.remove();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (e) { toast.error(formatErr(e)); }
    finally { setBusy(''); }
  };

  if (loading) {
    return <div className="p-12 flex justify-center text-slate-400"><Loader2 className="animate-spin" /></div>;
  }

  const published = review?.status === 'published';
  const m = review?.auto_metrics || {};

  return (
    <div className="space-y-5 pb-20" data-testid="my-performance-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-semibold text-slate-900 flex items-center gap-2">
            <Target size={20} className="text-teal-600" /> {t('My Performance')}
          </h1>
          <p className="text-sm text-slate-500 mt-0.5">
            {t("Your goals for this half-year, how you're tracking, and your appraisal.")}
          </p>
        </div>
        <div className="flex items-center gap-2">
          {/* desktop has no global top bar, so the switch lives here too */}
          <LanguagePicker className="hidden lg:inline-flex" />
          {payload.reviews.length > 1 && (
            <select
              className={`${inputCls} !w-auto`} value={activeId}
              onChange={(e) => setActiveId(e.target.value)} data-testid="my-cycle-select"
            >
              {payload.reviews.map((r) => <option key={r.id} value={r.id}>{r.cycle_name}</option>)}
            </select>
          )}
        </div>
      </div>

      {err && <div className="bg-red-50 border border-red-200 text-red-700 rounded-lg p-3 text-sm">{err}</div>}

      {(translating || sectionsLoading) && (
        <div
          className="flex items-center gap-2 bg-teal-50 border border-teal-200 text-teal-800 rounded-lg px-3 py-2 text-sm"
          data-testid="translating-banner"
        >
          <Loader2 size={15} className="animate-spin" />
          Translating this page… this takes a few seconds the first time.
        </div>
      )}

      <ModuleGuide meta={meta} />

      {!payload.staff && (
        <div className="bg-amber-50 border border-amber-200 text-amber-800 rounded-lg p-3 text-sm" data-testid="no-staff-record">
          {t("This login isn't linked to a staff record yet — ask the admin to add you to the Staff Master.")}
        </div>
      )}

      {payload.staff && !review && (
        <div className="bg-white border border-slate-200 rounded-xl p-8 text-center text-sm text-slate-500" data-testid="no-my-review">
          {t('No review cycle assigned to you yet. Your manager will set your goals soon.')}
        </div>
      )}

      {review && (
        <>
          <div className="bg-white border border-slate-200 rounded-xl p-4 flex flex-wrap items-center justify-between gap-4">
            <div>
              <div className="text-sm font-semibold text-slate-900">{review.cycle_name}</div>
              <div className="text-xs text-slate-500">{review.period_start} → {review.period_end}</div>
              <div className="mt-2 flex flex-wrap gap-1.5">
                <Badge className={STATUS_STYLE[review.status] || 'bg-slate-100 text-slate-600'} testid="my-review-status">
                  {t(statusLabel(review.status))}
                </Badge>
                <Badge className="bg-slate-100 text-slate-600">{t('Manager')}: {review.reporting_manager_name || '—'}</Badge>
              </div>
            </div>
            {published ? (
              <div className="flex items-center gap-3" data-testid="my-score-card">
                <ScoreRing value={review.final_score} band={review.band} />
                <div className="text-xs">
                  <div className="text-slate-500">{t('Final score')}</div>
                  <div className="text-lg font-semibold text-slate-900">{review.final_score}/100</div>
                  <Badge className={BAND_STYLE[review.band]}>{review.band} · {review.band_label}</Badge>
                </div>
                <button
                  onClick={downloadPdf} disabled={busy === 'pdf'}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-300 text-sm text-slate-700 hover:bg-slate-50"
                  data-testid="my-pdf-btn"
                >
                  {busy === 'pdf' ? <Loader2 size={15} className="animate-spin" /> : <FileDown size={15} />} {t('Appraisal PDF')}
                </button>
              </div>
            ) : (
              <div className="flex items-center gap-2" data-testid="score-locked">
                <div className="flex items-center gap-2 text-xs text-slate-500 bg-slate-50 border border-slate-200 rounded-lg px-3 py-2">
                  <Lock size={14} /> {t('Your score unlocks once management publishes this cycle.')}
                </div>
                <button
                  disabled
                  title={t('Your appraisal letter can be downloaded once this cycle is published')}
                  className="inline-flex items-center gap-1.5 px-3 py-2 rounded-lg border border-slate-200 text-sm text-slate-400 cursor-not-allowed"
                  data-testid="my-pdf-btn-locked"
                >
                  <FileDown size={15} /> {t('PDF')}
                </button>
              </div>
            )}
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 mb-3">
              <Activity size={15} className="text-teal-600" /> {t('My activity this cycle')}
              <span className="text-xs font-normal text-slate-400">{t('as of')} {m.as_of || '—'}</span>
            </h2>
            <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
              {[
                { label: t('Jobs completed'), value: m.jobs_completed ?? 0 },
                { label: t('On-time delivery'), value: `${m.on_time_pct ?? 0}%` },
                { label: t('Inspections'), value: m.inspections_done ?? 0 },
                { label: t('Attendance'), value: `${m.attendance_pct ?? 0}%` },
                { label: t('Days present'), value: `${m.days_present ?? 0}/${m.working_days ?? 0}` },
                { label: t('OT hours'), value: m.ot_hours ?? 0 },
                { label: t('Teammates lifted'), value: m.mentoring_credits ?? 0 },
              ].map((k) => (
                <div key={k.label} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                  <div className="text-[11px] uppercase tracking-wider text-slate-500">{k.label}</div>
                  <div className="text-base font-semibold text-slate-900">{k.value}</div>
                </div>
              ))}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 mb-3">
              <Target size={15} className="text-teal-600" /> {t('My goals')}
            </h2>
            <div className="space-y-3">
              {(review.goals_scored || review.goals || []).map((g, idx) => (
                <div key={g.id} className="rounded-lg border border-slate-200 p-3" data-testid={`my-goal-${idx}`}>
                  <div className="flex flex-wrap items-center justify-between gap-2">
                    <div>
                      <div className="text-sm font-medium text-slate-900">{g.title}</div>
                      <div className="text-xs text-slate-500">
                        {t('Weight')} {g.weight}% · {t('Target')} {fmtMetric(g.target, g.unit)}
                      </div>
                    </div>
                    <div className="text-right text-xs">
                      <div className="font-mono text-slate-900">{fmtMetric(g.actual_resolved, g.unit)}</div>
                      <div className="text-slate-500">{g.achieved_pct ?? '—'}% {t('Achieved')}</div>
                    </div>
                  </div>
                  <div className="mt-2"><ProgressBar pct={g.achieved_pct} /></div>
                  <input
                    className={`${inputCls} mt-2`} placeholder={t('Your note on this goal (optional)')}
                    value={draft.goal_notes[g.id] ?? ''}
                    disabled={published}
                    onChange={(e) => setDraft((s) => ({ ...s, goal_notes: { ...s.goal_notes, [g.id]: e.target.value } }))}
                    data-testid={`my-goal-note-${idx}`}
                  />
                  {published && g.manager_note && (
                    <p className="mt-2 text-xs text-slate-600 bg-slate-50 border border-slate-200 rounded p-2">
                      <span className="font-medium">{t('Manager')}:</span> {g.manager_note}
                    </p>
                  )}
                </div>
              ))}
              {!(review.goals || []).length && <p className="text-sm text-slate-500">{t('No goals set yet.')}</p>}
            </div>
          </div>

          <div className="bg-white border border-slate-200 rounded-xl p-4">
            <h2 className="text-sm font-semibold text-slate-800 mb-1">{t('My self assessment')}</h2>
            <p className="text-xs text-slate-500 mb-3">{t('Rate yourself 1 (low) to 5 (excellent). Your manager sees this before scoring.')}</p>
            <div className="space-y-2">
              {(meta?.competencies || []).map((c) => (
                <div key={c.key} className="flex flex-wrap items-center justify-between gap-2 border-b border-slate-100 pb-2">
                  <span className="text-sm text-slate-800">{c.label}</span>
                  <div className="flex items-center gap-4">
                    {published && (
                      <span className="text-xs text-slate-500">
                        {t('Manager')}: <span className="font-mono text-slate-800">{review.manager_competencies?.[c.key] ?? '—'}</span>
                      </span>
                    )}
                    <RatingPicker
                      value={draft.competencies[c.key]} disabled={published}
                      onChange={(n) => setDraft((s) => ({ ...s, competencies: { ...s.competencies, [c.key]: n } }))}
                      testid={`my-comp-${c.key}`}
                    />
                  </div>
                </div>
              ))}
            </div>
            <div className="mt-3">
              <label className="block text-xs text-slate-500 mb-1">{t("Anything you'd like management to know")}</label>
              <textarea
                rows={3} className={inputCls} value={draft.overall_comment} disabled={published}
                onChange={(e) => setDraft((s) => ({ ...s, overall_comment: e.target.value }))}
                data-testid="my-overall-comment"
              />
            </div>
            {!published && (
              <button
                onClick={submitSelf} disabled={busy === 'self'}
                className="mt-3 inline-flex items-center gap-1.5 px-4 py-2 rounded-lg bg-teal-600 text-white text-sm hover:bg-teal-700 disabled:opacity-50"
                data-testid="submit-self-btn"
              >
                {busy === 'self' ? <Loader2 size={15} className="animate-spin" /> : <Send size={15} />}
                {t(review.self_submitted_at ? 'Update self review' : 'Submit self review')}
              </button>
            )}
          </div>

          {published && (
            <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="published-feedback">              <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
                <Award size={15} className="text-teal-600" /> {t('Appraisal outcome')}
              </h2>
              {review.manager_overall_comment && (
                <p className="text-sm text-slate-700"><span className="text-slate-500">{t('Manager')}: </span>{review.manager_overall_comment}</p>
              )}
              {review.manager_strengths && (
                <p className="text-sm text-slate-700"><span className="text-slate-500">{t('Strengths')}: </span>{review.manager_strengths}</p>
              )}
              {review.manager_improvements && (
                <p className="text-sm text-slate-700"><span className="text-slate-500">{t('To improve')}: </span>{review.manager_improvements}</p>
              )}
              {review.admin_comment && (
                <p className="text-sm text-slate-700"><span className="text-slate-500">{t('Management')}: </span>{review.admin_comment}</p>
              )}
              {review.increment_pct !== null && review.increment_pct !== undefined && (
                <p className="text-sm font-medium text-emerald-700">{t('Approved increment')}: {review.increment_pct}%</p>
              )}
            </div>
          )}
        </>
      )}
      <AcknowledgeCard review={review} onAcknowledge={acknowledge} busy={busy} />

      <ScoreHistory reviews={payload.reviews} currentId={review?.id} />

      <MyRatingCard />

      <MySkillsCard
        data={mySkills}
        onCertificate={downloadCertificate}
        busy={busy}
        skillWeightPct={meta?.weights?.with_skills?.skills
          ? Math.round(meta.weights.with_skills.skills * 100) : null}
      />

      {(!!(myTraining.learning || []).length || !!(myTraining.teaching || []).length) && (
        <div className="bg-white border border-slate-200 rounded-xl p-4 space-y-3" data-testid="my-training-card">
          <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <GraduationCap size={15} className="text-teal-600" /> {t('My training')}
          </h2>
          {!!myTraining.plan?.summary && (
            <p className="text-sm text-slate-700 bg-teal-50/60 border border-teal-100 rounded-lg p-3"
               data-testid="my-plan-summary">
              {myTraining.plan.summary}
            </p>
          )}
          {!!(myTraining.learning || []).length && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">{t("Skills I'm learning")}</div>
              <div className="space-y-2">
                {myTraining.learning.map((row, n) => {
                  const overdue = (row.due_date || '') < myTraining.today && row.status !== 'done';
                  return (
                    <div key={row.id} className="rounded-lg border border-slate-200 p-3" data-testid={`my-training-${row.id}`}>
                      <div className="flex flex-wrap items-center gap-2">
                        <span className="text-[11px] font-semibold text-teal-700 bg-teal-50 border border-teal-100 rounded-full px-1.5 py-0.5">
                          {n + 1}
                        </span>
                        <span className="text-sm font-medium text-slate-900">{row.skill_name}</span>
                        <span className="text-xs text-slate-400">{row.skill_group}</span>
                        <span className="ml-auto px-1.5 py-0.5 rounded-full bg-slate-100 text-slate-600 text-xs">
                          {t(row.status.replace('_', ' '))}
                        </span>
                      </div>
                      {!!row.plan_method && (
                        <p className="text-sm text-slate-700 mt-1.5">{row.plan_method}</p>
                      )}
                      {!!row.plan_why && (
                        <p className="text-xs text-slate-500 mt-1">{row.plan_why}</p>
                      )}
                      <div className="flex flex-wrap items-center gap-x-3 gap-y-1 mt-1.5 text-xs text-slate-500">
                        <span>{row.trainer_name ? `${t('Trainer')}: ${row.trainer_name}` : t('Trainer to be assigned')}</span>
                        <span className={overdue ? 'text-red-600 font-medium' : ''}>
                          {t('due')} {row.due_date}
                        </span>
                        <span className="text-slate-400">
                          {t('Target')}: {row.target_level === 'expert' ? t('Expert — can train others') : t('OK')}
                        </span>
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          )}
          {!!(myTraining.teaching || []).length && (
            <div>
              <div className="text-[11px] uppercase tracking-wider text-slate-500 mb-1">
                {t("I'm training others in")}
              </div>
              <ul className="list-disc pl-5 text-sm text-slate-700">
                {myTraining.teaching.map((row) => (
                  <li key={row.id}>{row.staff_name} — {row.skill_name} ({t('due')} {row.due_date})</li>
                ))}
              </ul>
            </div>
          )}
        </div>
      )}

      {!!(payload.improvement_plans || []).length && (
        <div className="bg-white border border-amber-200 rounded-xl p-4 space-y-3" data-testid="my-pip-section">
          <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <LifeBuoy size={15} className="text-amber-600" /> My improvement plan
          </h2>
          {payload.improvement_plans.map((p) => (
            <div key={p.id} className="rounded-lg border border-slate-200 p-3" data-testid={`my-pip-${p.id}`}>
              <div className="flex flex-wrap items-center justify-between gap-2">
                <div className="text-xs text-slate-500">{p.start_date} → {p.end_date}</div>
                <Badge className={p.status === 'passed' ? 'bg-emerald-100 text-emerald-800'
                  : p.status === 'failed' ? 'bg-red-100 text-red-700' : 'bg-sky-100 text-sky-700'}>
                  {statusLabel(p.status)}
                </Badge>
              </div>
              <ul className="mt-2 space-y-1 text-sm text-slate-700 list-disc pl-5">
                {(p.objectives || []).map((o, i) => <li key={i}>{o}</li>)}
              </ul>
              <div className="grid gap-2 sm:grid-cols-3 mt-3">
                {(p.checkpoints || []).map((c) => (
                  <div key={c.day} className="rounded-lg border border-slate-200 bg-slate-50 px-3 py-2">
                    <div className="flex items-center justify-between text-xs">
                      <span className="font-semibold text-slate-800">{c.day}-day</span>
                      <span className="text-slate-500">{c.due_date}</span>
                    </div>
                    <div className={`text-[11px] mt-1 font-medium ${
                      c.status === 'met' ? 'text-emerald-700' : c.status === 'missed' ? 'text-red-600' : 'text-slate-500'
                    }`}>{statusLabel(c.status)}</div>
                    {c.notes && <div className="text-[11px] text-slate-600 mt-1">{c.notes}</div>}
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {!!(payload.salary_revisions || []).length && (
        <div className="bg-white border border-emerald-200 rounded-xl p-4" data-testid="my-revisions">
          <h2 className="text-sm font-semibold text-slate-800 flex items-center gap-1.5 mb-2">
            <Award size={15} className="text-emerald-600" /> My salary revisions
          </h2>
          {payload.salary_revisions.map((r) => (
            <div key={r.id} className="text-sm text-slate-700 flex flex-wrap items-center gap-2 py-1">
              <span className="font-medium text-emerald-700">+{r.increment_pct}%</span>
              <span className="text-xs text-slate-500">
                effective {r.effective_from} · {r.status === 'applied' ? 'applied' : 'scheduled'}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

export default MyPerformancePage;
